import { ENTITY_DEFS } from '~/data/entities'
import type { Row } from '~/composables/useDb'

export const PAGE_SIZE = 15

/** État des tables de la maquette : page, recherche, filtres, tri, sélection et menu de ligne ouvert. */
export function useTable() {
  return {
    page: useState<Record<string, number>>('tablePage', () => ({})),
    search: useState<Record<string, string>>('tableSearch', () => ({})),
    filters: useState<Record<string, Record<string, string>>>('tableFilters', () => ({})),
    sort: useState<{ key: string | null, field: string | null, dir: number }>('tableSort', () => ({ key: null, field: null, dir: 1 })),
    selected: useState<string[]>('selectedRows', () => []),
    selectedKey: useState<string | null>('selectedRowsKey', () => null),
    openMenu: useState<{ id: string, style: string } | null>('openRowMenu', () => null),
    currentRows: useState<Row[]>('currentTableRows', () => []),
    currentPageRows: useState<Row[]>('currentPageRows', () => []),
    barCount: useState<number | null>('barCount', () => null),
    bulkVisible: useState('bulkVisible', () => false),
  }
}

/** Ce que fait `goTo` d'une liste : retour à la première page, recherche effacée. */
export function resetTable(key: string): void {
  const t = useTable()
  t.bulkVisible.value = false
  t.page.value = { ...t.page.value, [key]: 1 }
  t.search.value = { ...t.search.value, [key]: '' }
}

export function setTableFilter(key: string, field: string, value: string): void {
  const t = useTable()
  t.bulkVisible.value = false
  const others = Object.fromEntries(Object.entries(t.filters.value[key] ?? {}).filter(([k]) => k !== field))
  t.filters.value = { ...t.filters.value, [key]: value === '' ? others : { ...others, [field]: value } }
  t.page.value = { ...t.page.value, [key]: 1 }
}

export function sortTable(key: string, field: string): void {
  const t = useTable()
  t.bulkVisible.value = false
  t.page.value = { ...t.page.value, [key]: 1 }
  const s = t.sort.value
  t.sort.value = s.key === key && s.field === field ? { ...s, dir: s.dir * -1 } : { key, field, dir: 1 }
}

export function setTablePage(key: string, n: number): void {
  const t = useTable()
  t.bulkVisible.value = false
  t.page.value = { ...t.page.value, [key]: n }
}

export function filterTableRows(key: string, value: string): void {
  const t = useTable()
  t.bulkVisible.value = false
  t.search.value = { ...t.search.value, [key]: value }
  t.page.value = { ...t.page.value, [key]: 1 }
}

export function toggleRowSelect(id: string, checked: boolean): void {
  const { selected, bulkVisible } = useTable()
  selected.value = checked ? [...new Set([...selected.value, id])] : selected.value.filter(x => x !== id)
  bulkVisible.value = selected.value.length > 0
}

export function toggleSelectAll(checked: boolean): void {
  const { selected, currentPageRows, bulkVisible } = useTable()
  const ids = currentPageRows.value.map(r => r.id)
  selected.value = checked ? [...new Set([...selected.value, ...ids])] : selected.value.filter(x => !ids.includes(x))
  bulkVisible.value = selected.value.length > 0
}

/** `toggleRowMenu` de la maquette : ouvre le menu « autres actions » d'une ligne, placé sous le bouton. */
export async function toggleRowMenu(evt: MouseEvent, key: string, id: string): Promise<void> {
  evt.stopPropagation()
  const { openMenu } = useTable()
  const menuId = `menu-${key}-${id}`
  if (openMenu.value?.id === menuId) {
    openMenu.value = null
    return
  }
  const btn = (evt.currentTarget as HTMLElement) || (evt.target as HTMLElement).closest('button')!
  const rect = btn.getBoundingClientRect()
  openMenu.value = { id: menuId, style: '' }
  await nextTick()   // le menu doit être affiché pour mesurer sa taille
  const el = document.getElementById(menuId)
  const menuWidth = el?.offsetWidth || 150
  let left = rect.right - menuWidth
  if (left < 8) left = 8
  let top = rect.bottom + 4
  const menuHeight = el?.offsetHeight || 120
  if (top + menuHeight > window.innerHeight - 8) top = rect.top - menuHeight - 4
  openMenu.value = { id: menuId, style: `position: fixed; top: ${top}px; left: ${left}px; right: auto;` }
}

function errorMessage(error: unknown): string {
  const e = error as { data?: { detail?: string } }
  return e?.data?.detail || 'Opération impossible.'
}

async function apiDelete(collection: string, id: string): Promise<void> {
  await $fetch(`/api/${collection}/${id}`, { method: 'DELETE' })
  const db = useDb()
  db.value = { ...db.value, [collection]: (db.value[collection] ?? []).filter(r => r.id !== id) }
}

/** `recomputeEcheancierRemise` de la maquette : la remise d'un échéancier est la somme de ses réductions appliquées. */
export async function recomputeEcheancierRemise(inscriptionId: unknown): Promise<void> {
  const db = useDb().value
  const echeancier = (db.echeanciers ?? []).find(e => e.inscriptionId === inscriptionId)
  if (!echeancier) return
  const total = (db.reductionsAppliquees ?? []).filter(r => r.inscriptionId === inscriptionId).reduce((s, r) => s + Number(r.montantCalcule || 0), 0)
  const updated = { ...echeancier, montantRemise: total, montantNet: Number(echeancier.montantBrut) - total }
  const saved = await $fetch<Row>(`/api/echeanciers/${echeancier.id}`, { method: 'PUT', body: updated })
  const store = useDb()
  store.value = { ...store.value, echeanciers: (store.value.echeanciers ?? []).map(e => (e.id === saved.id ? saved : e)) }
}

/** `deleteRow` de la maquette. */
export async function deleteRow(key: string, id: string): Promise<void> {
  const entity = ENTITY_DEFS[key]!
  const storeKey = entity.storageKey || key
  if (!window.confirm(`Supprimer cet enregistrement « ${entity.label} » ? Cette action est irréversible.`)) return
  const row = findById(storeKey, id)
  try {
    await apiDelete(storeKey, id)
    useTable().bulkVisible.value = false
    showToast(`${entity.label} supprimé(e).`)
    if (key === 'reductionsAppliquees' && row) await recomputeEcheancierRemise(row.inscriptionId)
  }
  catch (error) {
    showToast(errorMessage(error))
  }
}

/** `bulkDeleteSelected` de la maquette. */
export async function bulkDeleteSelected(key: string): Promise<void> {
  const { selected } = useTable()
  if (selected.value.length === 0) return
  if (!window.confirm(`Supprimer ${selected.value.length} enregistrement(s) sélectionné(s) ?`)) return
  const entity = ENTITY_DEFS[key]!
  const storeKey = entity.storageKey || key
  try {
    for (const id of [...selected.value]) await apiDelete(storeKey, id)
    selected.value = []
    useTable().bulkVisible.value = false
    showToast('Éléments supprimés.')
  }
  catch (error) {
    showToast(errorMessage(error))
  }
}

/** Export Excel d'une liste : le fichier est écrit par le backend, avec toutes les colonnes de l'entité pour les lignes affichées. */
export function exportCSV(key: string): void {
  const { currentRows } = useTable()
  exportEntityXlsx(key, currentRows.value.map(row => row.id as string))
}
